/**
 * Per-preset day-response map — the PHASE-2 directional atmosphere config.
 *
 * Phase 1 ships a single uniform `mood` scalar (quiet at night, lively at noon)
 * that scales every mooded layer at once. That's deliberately gentle because a
 * big uniform swing mis-drives directional layers (it would thin fog at night —
 * the opposite of the intent). This table is the **directional** counterpart:
 * it names which layer kinds / properties on each preset respond to the clock,
 * and how, so the TimeOfDayDriver can drive per-layer modulators by phase.
 *
 * Each entry maps a preset id → a list of **day-keyed knobs**. A knob names:
 *  - `kind`: the EffectLayer kind it targets (e.g. 'godRays', 'fog', 'glowBlobs')
 *  - `property`: the canonical property on that layer's spec (e.g. 'alphaMax')
 *  - `phaseMultipliers`: a per-phase multiplier on the authored base value
 *    (night / dawn / day / dusk). 1 = unchanged; >1 = amplified; <1 = dampened.
 *
 * The driver composes: `effective = authored × phaseMultiplier(phase)`. The
 * authored value is the preset's own spec value — the day clock shapes it, it
 * doesn't overwrite it. Interiors + always-night maps (see `INTERIOR_PRESETS` /
 * `ALWAYS_NIGHT_MAPS` in `timeOfDay.config.ts`) are exempt and have NO entry
 * here — their eternal light rules.
 *
 * @see docs/design/TIME_OF_DAY.md §5b — the phase-2 modulator global-phase hook.
 * @see timeOfDay.config.ts — the phase-1 mood axis + the opt-out sets.
 */
import type { TimeOfDayPhase } from './timeOfDay.config';

export interface DayKeyedKnob {
  /** The EffectLayer kind this knob targets. */
  kind: string;
  /** The canonical property on that layer's spec (e.g. 'alphaMax', 'count'). */
  property: string;
  /** Per-phase multiplier on the authored base value (1 = unchanged). */
  phaseMultipliers: Record<TimeOfDayPhase, number>;
}

export type DayResponseMap = Record<string, DayKeyedKnob[]>;

// ─── The per-preset day-response table ───────────────────────────────────────
//
// Design principles:
//  • godRays are a DAY phenomenon — dampened at night, strongest at noon.
//  • fireflies / motes are a NIGHT phenomenon — brighter at night, dim by day.
//  • fog thickens at night (the Coil's dread deepens after dusk).
//  • glowBlobs (lanterns, hearths) warm UP at night (the cozy counterpoint).
//  • celestial (sun) tracks the clock directly — small/dim at night, big/bright
//    at noon, warm-haloed at dawn/dusk.
//  • weather (drizzle, leaves) gusts harder at dusk/night (the wind picks up).
//
// Each biome's blueprint (docs/design/biomes/) specifies its day-keyed knobs;
// this table is the formalization. Presets not listed here get the default
// uniform mood response only (phase 1).
export const DAY_RESPONSE: DayResponseMap = {
  // ── Hollow Coil cozy→spooky gradient ──────────────────────────────────────
  pig_beach: [
    // The brightest beat — day swings MOST. Sun tracks the clock; godRays by day.
    { kind: 'celestial', property: 'coreAlpha', phaseMultipliers: { night: 0.3, dawn: 0.7, day: 1.0, dusk: 0.6 } },
    { kind: 'celestial', property: 'haloAlpha', phaseMultipliers: { night: 0.1, dawn: 1.2, day: 1.0, dusk: 1.3 } },
    { kind: 'godRays', property: 'alphaMax', phaseMultipliers: { night: 0.1, dawn: 0.6, day: 1.0, dusk: 0.5 } },
    { kind: 'godRays', property: 'alphaMin', phaseMultipliers: { night: 0.05, dawn: 0.5, day: 1.0, dusk: 0.4 } },
    // Dusk rhymes with tidewend's amber — the grade warms.
    { kind: 'colorGrade', property: 'strength', phaseMultipliers: { night: 0.8, dawn: 1.1, day: 0.9, dusk: 1.2 } },
  ],
  tidewend: [
    // The warm pivot — lanterns warm UP at night (the cozy counterpoint to the
    // encroaching dark). A faint fog creeps in at night.
    { kind: 'glowBlobs', property: 'alpha', phaseMultipliers: { night: 1.4, dawn: 0.9, day: 0.7, dusk: 1.1 } },
    { kind: 'flicker', property: 'baseAlpha', phaseMultipliers: { night: 1.5, dawn: 1.0, day: 0.6, dusk: 1.2 } },
    { kind: 'flicker', property: 'flickerAmp', phaseMultipliers: { night: 1.3, dawn: 1.0, day: 0.8, dusk: 1.1 } },
    // The vignette cool wash deepens at night — the first unease.
    { kind: 'vignette', property: 'edgeAlpha', phaseMultipliers: { night: 1.3, dawn: 0.9, day: 0.8, dusk: 1.1 } },
  ],
  // brackenmoor / coil_undercroft: NO ENTRY — always-night authored scenes.

  // ── Cross-biome leviathan showcases ───────────────────────────────────────
  sunken_drift: [
    // Interior vault — but not fully exempt (it has a sky gradient). The cold
    // caustics dim at night, the motes brighten.
    { kind: 'fireflies', property: 'alphaMax', phaseMultipliers: { night: 1.4, dawn: 1.0, day: 0.7, dusk: 1.1 } },
    { kind: 'glowBlobs', property: 'alpha', phaseMultipliers: { night: 1.3, dawn: 1.0, day: 0.8, dusk: 1.1 } },
  ],
  sky_whale: [
    // Bright sky — the sun tracks the clock; godRays by day.
    { kind: 'celestial', property: 'coreAlpha', phaseMultipliers: { night: 0.2, dawn: 0.8, day: 1.0, dusk: 0.5 } },
    { kind: 'godRays', property: 'alphaMax', phaseMultipliers: { night: 0.1, dawn: 0.6, day: 1.0, dusk: 0.5 } },
  ],
  // void_serpent: INTERIOR (enclosed abyss) — exempt, no entry.
  // fossil_graveyard: INTERIOR (dry stone vault) — exempt, no entry.

  // ── Existing outdoor biomes (the default directional response) ────────────
  cozy_forest: [
    { kind: 'godRays', property: 'alphaMax', phaseMultipliers: { night: 0.1, dawn: 0.6, day: 1.0, dusk: 0.5 } },
    { kind: 'fireflies', property: 'alphaMax', phaseMultipliers: { night: 1.5, dawn: 1.0, day: 0.6, dusk: 1.2 } },
  ],
  meadow: [
    { kind: 'godRays', property: 'alphaMax', phaseMultipliers: { night: 0.1, dawn: 0.6, day: 1.0, dusk: 0.5 } },
    { kind: 'fireflies', property: 'alphaMax', phaseMultipliers: { night: 1.4, dawn: 1.0, day: 0.7, dusk: 1.1 } },
  ],
  sakura: [
    { kind: 'godRays', property: 'alphaMax', phaseMultipliers: { night: 0.1, dawn: 0.6, day: 1.0, dusk: 0.5 } },
    { kind: 'fireflies', property: 'alphaMax', phaseMultipliers: { night: 1.3, dawn: 1.0, day: 0.7, dusk: 1.1 } },
  ],
  coast: [
    { kind: 'godRays', property: 'alphaMax', phaseMultipliers: { night: 0.1, dawn: 0.6, day: 1.0, dusk: 0.5 } },
    { kind: 'celestial', property: 'coreAlpha', phaseMultipliers: { night: 0.2, dawn: 0.8, day: 1.0, dusk: 0.5 } },
  ],
  moor: [
    { kind: 'fog', property: 'alphaMax', phaseMultipliers: { night: 1.4, dawn: 1.0, day: 0.8, dusk: 1.2 } },
    { kind: 'celestial', property: 'coreAlpha', phaseMultipliers: { night: 0.1, dawn: 0.5, day: 1.0, dusk: 0.4 } },
  ],
  autumn: [
    { kind: 'glowBlobs', property: 'alpha', phaseMultipliers: { night: 1.3, dawn: 1.0, day: 0.7, dusk: 1.1 } },
    { kind: 'fireflies', property: 'alphaMax', phaseMultipliers: { night: 1.4, dawn: 1.0, day: 0.6, dusk: 1.2 } },
  ],
  celestial: [
    { kind: 'fireflies', property: 'alphaMax', phaseMultipliers: { night: 1.5, dawn: 1.1, day: 0.8, dusk: 1.2 } },
  ],
  twilight: [
    { kind: 'glowBlobs', property: 'alpha', phaseMultipliers: { night: 1.3, dawn: 1.0, day: 0.8, dusk: 1.1 } },
    { kind: 'fireflies', property: 'alphaMax', phaseMultipliers: { night: 1.4, dawn: 1.0, day: 0.7, dusk: 1.2 } },
  ],
};

/** The day-keyed knobs for a preset (empty array = uniform mood response only). */
export function dayResponseFor(presetId: string): DayKeyedKnob[] {
  return DAY_RESPONSE[presetId] ?? [];
}

/** The phase multiplier for a knob at a given phase (1 = unchanged). */
export function phaseMultiplierFor(knob: DayKeyedKnob, phase: TimeOfDayPhase): number {
  return knob.phaseMultipliers[phase] ?? 1;
}

// ─── Phase-2 runtime composition helpers (pure, TDD'd) ───────────────────────
//
// The AtmosphereLayer modulation tick calls these each frame to fold the day
// clock's directional response into a layer's opacity/speed/density. A knob's
// `property` names the authored spec field it conceptually scales; at runtime
// the unified dial is the layer's container alpha (opacity), the emitter speed
// (speed), or the emitter density (count). These helpers map a knob's property
// onto the right dial so the apply-loop stays generic.

/** Properties that scale a layer's OPACITY (the container alpha dial). Every
 *  alpha/opacity-style authored field folds into the one container multiplier. */
const OPACITY_PROPERTIES = new Set([
  'alphaMax', 'alphaMin', 'alpha', 'coreAlpha', 'haloAlpha',
  'baseAlpha', 'flickerAmp', 'edgeAlpha',
]);
/** Properties that scale a layer's emitter SPEED (the setSpeed dial). */
const SPEED_PROPERTIES = new Set(['speed']);
/** Properties that scale a layer's emitter DENSITY (the setDensity dial). */
const DENSITY_PROPERTIES = new Set(['count', 'density']);

/** The opacity multiplier for a preset's layers of `kind` at `phase` (1 = no
 *  response). Folds every alpha-property knob for that kind into one product. */
export function dayOpacityMultiplier(knobs: DayKeyedKnob[], kind: string, phase: TimeOfDayPhase): number {
  let m = 1;
  let found = false;
  for (const k of knobs) {
    if (k.kind !== kind) continue;
    if (!OPACITY_PROPERTIES.has(k.property)) continue;
    m *= phaseMultiplierFor(k, phase);
    found = true;
  }
  return found ? m : 1;
}

/** The speed multiplier for a preset's layers of `kind` at `phase` (1 = no
 *  response). Folds every speed-property knob for that kind into one product. */
export function daySpeedMultiplier(knobs: DayKeyedKnob[], kind: string, phase: TimeOfDayPhase): number {
  let m = 1;
  let found = false;
  for (const k of knobs) {
    if (k.kind !== kind) continue;
    if (!SPEED_PROPERTIES.has(k.property)) continue;
    m *= phaseMultiplierFor(k, phase);
    found = true;
  }
  return found ? m : 1;
}

/** The density multiplier for a preset's layers of `kind` at `phase` (1 = no
 *  response). Folds every density-property knob for that kind into one product. */
export function dayDensityMultiplier(knobs: DayKeyedKnob[], kind: string, phase: TimeOfDayPhase): number {
  let m = 1;
  let found = false;
  for (const k of knobs) {
    if (k.kind !== kind) continue;
    if (!DENSITY_PROPERTIES.has(k.property)) continue;
    m *= phaseMultiplierFor(k, phase);
    found = true;
  }
  return found ? m : 1;
}

/** The colorGrade `strength` multiplier for a preset at `phase` (1 = no response).
 *  Folds every colorGrade-strength knob into one product (the grade composition
 *  in AtmosphereLayer applies it on top of the time-grade blend). */
export function dayGradeStrengthMultiplier(knobs: DayKeyedKnob[], phase: TimeOfDayPhase): number {
  let m = 1;
  let found = false;
  for (const k of knobs) {
    if (k.kind !== 'colorGrade' || k.property !== 'strength') continue;
    m *= phaseMultiplierFor(k, phase);
    found = true;
  }
  return found ? m : 1;
}
